import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SortableDataTableRowProps {
  id: string;
  disabled?: boolean;
  dragHandleLabel: string;
  className?: string;
  children: ReactNode;
  /** When set, the row is keyboard-activatable (Enter / Space). */
  activateLabel?: string;
  onActivate?: () => void;
}

export function SortableDataTableRow({
  id,
  disabled = false,
  dragHandleLabel,
  className,
  children,
  activateLabel,
  onActivate,
}: SortableDataTableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const rowActivatable = activateLabel != null && onActivate != null;

  return (
    <tr
      ref={setNodeRef}
      style={{
        // Do not translate the in-flow row while dragging — a large translate
        // expands the scroll container's scrollHeight and auto-scroll races.
        // The DragOverlay carries the pointer-following preview instead.
        transform: isDragging
          ? undefined
          : CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : undefined,
      }}
      className={cn(
        className,
        isDragging && "relative z-10 bg-surface-50",
      )}
      tabIndex={rowActivatable ? 0 : undefined}
      aria-label={rowActivatable ? activateLabel : undefined}
      onClick={rowActivatable ? onActivate : undefined}
      onKeyDown={
        rowActivatable
          ? (event) => {
              if (event.target !== event.currentTarget) {
                return;
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onActivate();
              }
            }
          : undefined
      }
    >
      <td className="w-0 whitespace-nowrap px-4 py-3">
        <button
          type="button"
          className={cn(
            "touch-none rounded p-1 text-surface-400",
            disabled
              ? "cursor-default opacity-40"
              : "cursor-grab hover:bg-surface-100 hover:text-surface-700 active:cursor-grabbing",
          )}
          aria-label={dragHandleLabel}
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      </td>
      {children}
    </tr>
  );
}
